import { useEffect, useMemo, useState, type ChangeEvent } from "react";

import { ChatComposer } from "./ChatComposer";
import { ChatHeader, type ChatStatus } from "./ChatHeader";
import { ChatSearchBar } from "./ChatSearchBar";
import { ChatThread } from "./ChatThread";
import { RosterCard } from "./RosterCard";
import { findHits } from "./chatSearch";
import type { CoordinatorWorkspace } from "./coordinator";

type Lang = "hr" | "en";

const dollars = (n: number) => `${(n / 1000000).toFixed(4)} USD`;

export function CoordinatorChat(p: {
  data: CoordinatorWorkspace | null;
  error: string;
  busy: boolean;
  uncertain: boolean;
  disabled: boolean;
  message: string;
  setMessage: (v: string) => void;
  sources: string;
  setSources: (v: string) => void;
  allowResearch: boolean;
  setAllowResearch: (v: boolean) => void;
  consent: boolean;
  setConsent: (v: boolean) => void;
  onSubmit: () => void;
  onEnable: () => void;
  onRefresh: () => void;
  projectName: string;
  currentProjectId: string;
  projects: { id: string; name: string; agent: boolean }[];
  onSelectProject: (id: string) => void;
  lang: Lang;
  now?: Date;
  seenId: string;
}) {
  const { data, lang } = p;
  const en = lang === "en";

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [infoOpen, setInfoOpen] = useState(false);

  const messages = useMemo(() => data?.messages ?? [], [data]);

  const hits = useMemo(
    () => (searchOpen ? findHits(messages, query) : []),
    [searchOpen, messages, query],
  );

  useEffect(() => {
    if (!searchOpen) return;
    if (active >= hits.length) setActive(hits.length - 1);
  }, [searchOpen, hits.length, active]);

  function closeSearch() {
    setSearchOpen(false);
    setQuery("");
    setActive(-1);
  }

  function toggleSearch() {
    if (searchOpen) {
      closeSearch();
    } else {
      setSearchOpen(true);
    }
  }

  function handleQuery(v: string) {
    setQuery(v);
    setActive(findHits(messages, v).length - 1);
  }

  function handleNext() {
    if (hits.length === 0) return;
    setActive((active + 1) % hits.length);
  }

  function handlePrev() {
    if (hits.length === 0) return;
    setActive((active - 1 + hits.length) % hits.length);
  }

  let status: ChatStatus;
  if (!data) {
    status = { kind: "notready", text: en ? "Loading…" : "Učitavanje…" };
  } else if (p.uncertain) {
    status = { kind: "uncertain", text: en ? "Check status" : "Provjeri status" };
  } else if (p.busy || data.pending) {
    status = {
      kind: "working",
      text: en ? "The agent is preparing an answer…" : "Agent priprema odgovor…",
    };
  } else if (!data.provider_ready) {
    status = { kind: "notready", text: en ? "Not ready" : "Nije spreman" };
  } else if (!data.enabled) {
    status = { kind: "off", text: en ? "Not enabled" : "Nije uključen" };
  } else {
    status = { kind: "ready", text: en ? "Ready" : "Spreman" };
  }

  return (
    <section
      data-testid="coordinator-panel"
      className="flex h-full min-h-0 flex-1 flex-col bg-background"
    >
      <div className="relative shrink-0">
        <ChatHeader
          projectName={p.projectName}
          conversationTitle={en ? "Main conversation" : "Glavni razgovor"}
          status={status}
          lang={lang}
          projects={p.projects}
          currentProjectId={p.currentProjectId}
          onSelectProject={p.onSelectProject}
          searchOpen={searchOpen}
          infoOpen={infoOpen}
          onToggleSearch={toggleSearch}
          onToggleInfo={() => setInfoOpen((v) => !v)}
        />

        {searchOpen && (
          <ChatSearchBar
            query={query}
            onQuery={handleQuery}
            count={hits.length}
            active={active}
            onPrev={handlePrev}
            onNext={handleNext}
            onClose={closeSearch}
            lang={lang}
          />
        )}

        <aside
          data-testid="chat-info"
          hidden={!infoOpen}
          className="absolute inset-x-0 top-full z-20 max-h-[60dvh] space-y-3 overflow-y-auto border-b border-border bg-background p-3 shadow-lg"
        >
          {data && <RosterCard roster={data.roster} />}

          {data && (
            <section className="rounded-xl border border-border p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <h3 className="font-semibold">Budžet razgovora</h3>
                <span className="text-sm">{data.enabled ? "Uključen" : "Nije uključen"} · ukupni limit {dollars(data.budget.limit_micros)}</span>
              </div>
              <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
                <div><dt className="text-muted-foreground">Procjena iz tokena</dt><dd>{dollars(data.budget.spent_micros)}</dd></div>
                <div><dt className="text-muted-foreground">Rezervirano</dt><dd>{dollars(data.budget.reserved_micros)}</dd></div>
                <div><dt className="text-muted-foreground">Nepoznat obračun</dt><dd>{dollars(data.budget.unknown_micros)}</dd></div>
              </dl>
              <p className="mt-3 text-xs text-muted-foreground">Ovo je zaseban kumulativni budžet za razgovor, bez resetiranja potrošnje. Nije budžet istraživanja 100 leadova niti račun pružatelja.</p>
            </section>
          )}

          <details className="rounded-xl border border-border p-4 text-sm">
            <summary className="cursor-pointer font-semibold">Što je povezano?</summary>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>DeepSeek: razgovor i prijedlozi zadataka uz tvoj budžet.</li>
              <li>Explee, LeadOcean i ChooseFlow: alati se dodjeljuju po projektu u Integrations; izvršenje i rezultati imaju zaseban zapis. Opće pregledavanje interneta nije uključeno.</li>
              <li>Zia: potrebna je konfiguracija i provjera dostupnosti.</li>
              <li>Bigin slanje i automatsko izvršavanje prijedloga nisu uključeni.</li>
            </ul>
          </details>

          <button
            type="button"
            onClick={p.onRefresh}
            disabled={p.busy}
            className="min-h-11 text-sm underline disabled:opacity-50"
          >
            {en ? "Check status" : "Provjeri status"}
          </button>
        </aside>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {data && !data.enabled && (
          <div
            data-testid="chat-enable-card"
            className="mx-3 mt-3 shrink-0 space-y-3 rounded-xl border border-border p-3"
          >
            {!data.provider_ready && (
              <p role="status" className="text-sm">
                Koordinator nije spreman: provjerava se veza s radnikom i konfiguracija
                pružatelja. Slanje je zaustavljeno.
              </p>
            )}
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={p.consent}
                disabled={p.busy || !data.provider_ready}
                onChange={(e: ChangeEvent<HTMLInputElement>) => p.setConsent(e.target.checked)}
              />
              <span>Odobravam najviše 1 USD ukupno za ovaj DeepSeek razgovor.</span>
            </label>
            <button
              type="button"
              onClick={p.onEnable}
              disabled={!p.consent || !data.provider_ready || p.busy}
              className="min-h-11 rounded-lg border border-border px-3 text-sm disabled:opacity-50"
            >
              Uključi DeepSeek
            </button>
          </div>
        )}

        {p.error && (
          <p
            role="alert"
            className="mx-3 mt-3 shrink-0 rounded-lg border border-destructive/40 p-3 text-sm"
          >
            {p.error}
          </p>
        )}

        {!data && (
          <p data-testid="chat-loading" role="status" className="p-3 text-sm">
            {en ? "Loading the conversation…" : "Učitavanje razgovora…"}
          </p>
        )}

        {p.uncertain && (
          <div
            data-testid="chat-uncertain"
            className="mx-3 mt-3 shrink-0 space-y-2 rounded-lg border border-amber-500/50 p-3 text-sm"
          >
            <p>
              Prihvat poruke nije potvrđen. Provjeri status prije novog pokušaja; automatsko
              ponovno slanje je zaustavljeno.
            </p>
            <button
              type="button"
              onClick={p.onRefresh}
              disabled={p.busy}
              className="min-h-11 underline disabled:opacity-50"
            >
              {en ? "Check status" : "Provjeri status"}
            </button>
          </div>
        )}

        {data && (
          <ChatThread
            messages={data.messages}
            pending={data.pending}
            lang={lang}
            now={p.now ?? new Date()}
            seenId={p.seenId}
            query={searchOpen ? query : ""}
            activeHit={searchOpen ? active : -1}
          />
        )}
      </div>

      <ChatComposer
        value={p.message}
        onChange={p.setMessage}
        onSubmit={p.onSubmit}
        disabled={p.disabled}
        busy={p.busy}
        sources={p.sources}
        onSources={p.setSources}
        allowResearch={p.allowResearch}
        onAllowResearch={p.setAllowResearch}
        lang={lang}
      />
    </section>
  );
}
